import logoUrl from '@/assets/logo_white.png';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import { default as MuiToolbar } from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useEffect, type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hasArrived, type PendingNavigation } from './pendingNavigation';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { fillHeight } from '../../theme';
import { useCurrentBrand } from '../../hooks/useCurrentBrand';
import Spacer from '../common/Spacer';
import DrawerItems from './DrawerItems';
import Routes from './Routes';
import Toolbar from './Toolbar';
import { useAppContext } from '../../AppContext';

const drawerWidth = 240;

const Frame = (): ReactElement => {
    const strings = useLocalizedStrings();
    //const [title, setTitle] = useState("");
    const app = useAppContext();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { data: currentBrand } = useCurrentBrand();

    // If the selected brand doesn't have TouchTab Feedback enabled, don't leave the user stranded on a
    // feedback page (its menu is hidden) — send them home. Fires on brand switch and on direct navigation.
    useEffect(() => {
        if (!currentBrand) return;
        const feedbackEnabled = currentBrand.touchTabFeedbackEnabled ?? false;
        const onFeedbackPage = routerLocation.pathname.includes('/feedback/');
        if (!feedbackEnabled && onFeedbackPage) {
            app.setTitle('');
            navigate(`/${app.organizationSlug}`);
        }
    }, [currentBrand, routerLocation.pathname]);

    // The title used to be set the instant a menu item was clicked, so choosing "Stay" on the unsaved-changes
    // prompt left the bar naming a page the user never reached. Hold the intended title and apply it only once
    // the route has actually moved to the page it belongs to — a blocked navigation therefore changes nothing.
    // Matched on the clicked item's DESTINATION PATH. This used to compare pathname.split('/')[1] against the
    // menu item's key, but on every route in this app that segment is the ORG SLUG — so it never matched and
    // the title was never applied at all. The path is what the router actually moves to, so it can be
    // compared directly (TTD-151).
    const pendingTitle = React.useRef<PendingNavigation<string> | null>(null);

    const handleDrawerListItemClick = (_event: React.MouseEvent<HTMLElement>, _key: string, text: string, path: string) => {
        pendingTitle.current = { path, value: text };
    };

    useEffect(() => {
        const pending = pendingTitle.current;
        if (!pending) return;

        // Only apply it if we actually landed on the page that title was for. Guards the case where a
        // navigation was blocked and the user later moved somewhere else entirely.
        if (hasArrived(pending, routerLocation.pathname)) app.setTitle(pending.value);
        pendingTitle.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routerLocation.pathname]);

    const handleHomeClick = () => {
        app.setTitle("");
    };

    const handleHelpClick = () => {
        console.log("Help")
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
            <CssBaseline />
            <AppBar position="fixed" enableColorOnDark sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <MuiToolbar>
                    <img src={logoUrl} alt="TouchTabLogo" style={{ width: 192, border: 0 }} />
                    <Spacer x={5} />
                    <Typography variant="h6" fontWeight="700" noWrap component="div">{app.title}</Typography>
                    <Toolbar
                        onHomeClick={handleHomeClick}
                        onHelpClick={handleHelpClick}
                    />
                </MuiToolbar>
            </AppBar>
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        //backgroundColor: (theme) => theme.palette.secondary.light,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        overflowX: 'hidden',
                        height: '100%',
                    },
                }}
            >
                <Box sx={{ flexGrow: 1 }}>
                    <MuiToolbar />
                    <DrawerItems onItemClick={handleDrawerListItemClick} />
                </Box>
                <Typography variant="caption" sx={{ textAlign: 'center', pb: 2 }}>
                    {strings.copyright.replace("{{year}}", new Date().getFullYear().toString())}
                </Typography>
            </Drawer>
            <Box component="main" sx={{ ...fillHeight, overflow: 'hidden' }}>
                <MuiToolbar />                
                <Routes />                
            </Box>
        </Box>
    );
}

export default Frame;
